import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasRole, type AuthSession, type Role, type User } from '@eon/types';
import { api } from '../client';
import { setAccessToken } from '../tokens';

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginOptions {
  /**
   * If provided, the login flow will fetch `/my/profile` after authenticating
   * and reject (logout + throw) if the user's role is not in the allowlist.
   * Use this in apps that should only admit a specific role tier
   * (e.g. the system admin app should pass `['system_admin']`).
   */
  requireRoles?: readonly Role[];
  /** Custom error message for the role guard. */
  requireRolesError?: string;
}

interface LoginResponse {
  accessToken: string;
  appType?: string;
}

export function useLogin(options: LoginOptions = {}) {
  const qc = useQueryClient();
  const { requireRoles, requireRolesError } = options;
  return useMutation({
    mutationFn: async (input: LoginInput): Promise<AuthSession> => {
      const res = await api().post<LoginResponse>('/auth/login', input, { anonymous: true });
      setAccessToken(res.accessToken);

      // Backend's /auth/login intentionally only returns the access token +
      // appType (parity with the mobile contract). Pull the full profile so
      // the rest of the app has a usable `me` object and we can apply any
      // app-level role guards before navigating.
      let user: User | undefined;
      try {
        user = await api().get<User>('/my/profile');
      } catch (err) {
        setAccessToken(null);
        throw err;
      }

      if (requireRoles && requireRoles.length > 0 && !hasRole(user?.role, requireRoles)) {
        setAccessToken(null);
        try {
          await api().post('/auth/logout', undefined);
        } catch {
          // best-effort revoke
        }
        throw new Error(
          requireRolesError ??
            'This account is not authorised to use this application. Please sign in with an account that has the required role.',
        );
      }

      qc.setQueryData(['me'], user);
      return { accessToken: res.accessToken, appType: (res.appType ?? user?.appType ?? 'system') as AuthSession['appType'], user };
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await api().post('/auth/logout', undefined);
      } finally {
        setAccessToken(null);
      }
    },
    onSuccess() {
      qc.clear();
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api().get<User>('/my/profile'),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: { email: string }) =>
      api().post<{ message: string }>('/auth/forgot-password', input, { anonymous: true }),
  });
}

export interface ChangePasswordInput {
  oldPassword: string;
  newPassword: string;
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api().post<{ message: string }>('/auth/change-password', input),
  });
}
